import {gsap} from "../LibrariesImport";
import {GlobalVariables} from "../GlobalVariables";

export default class Shapes {

    static init() {
        this.totalFrames = 50;
        this.shapeAnimations = document.querySelectorAll(".shape-animation");
        this.shapeAnimations.forEach((animation) => {
            this.build(animation);
        });

        GlobalVariables.resizeCallbacks.push(this);
    }

    static build(animation) {
        animation.canvas = document.createElement("canvas");
        animation.canvas.width = animation.clientWidth;
        animation.canvas.height = animation.clientHeight;
        animation.appendChild(animation.canvas);
        animation.ctx = animation.canvas.getContext("2d");

        this.preloadImages(animation);
    }

    static preloadImages(animation) {
        const animationNumber = animation.dataset.animationNumber;
        const animationSize = animation.dataset.animationSize ?? "large";
        animation.imagesLoaded = 0;
        animation.images = [];

        for (let i = 1; i <= this.totalFrames; i++) {
            const img = new Image();
            img.src = `${mcArgs.themeLink}/images/shapes/${animationSize}/shape-${animationNumber}/${String(i).padStart(4, "0")}.webp`;
            img.onload = () => {
                animation.imagesLoaded++;
                if (animation.imagesLoaded === this.totalFrames) {
                    this.onAllImagesLoaded(animation);
                }
            };
            animation.images.push(img);
        }
    }

    static onAllImagesLoaded(animation) {
        animation.frameObj = { frame: 1 };

        if (animation.dataset.animationAutoplay === "true") {
            this.initTweenLoop(animation);
        } else {
            this.drawFrame(animation);
            this.initHover(animation);
        }
    }

    static initHover(animation) {
        // Elements elsewhere in the page that control this animation
        let triggers = [];
        if (animation.id) {
            triggers = Array.from(document.querySelectorAll(`[data-target-animation="${animation.id}"]`));
        }

        // Fallback: allow hovering the animation block itself if no external trigger is found
        if (!triggers.length) {
            triggers = [animation];
        }

        const start = () => {
            // If a tween exists, resume if paused; otherwise do nothing (already running)
            if (animation.currentTween) {
                if (animation.currentTween.paused && animation.currentTween.paused()) {
                    animation.currentTween.resume();
                    return;
                }
                if (animation.currentTween.isActive && animation.currentTween.isActive()) return;
            }
            // No tween yet: start from current frame (kept in frameObj if any)
            if (!animation.frameObj) animation.frameObj = { frame: 0 };
            this.initTweenLoop(animation);
        };

        const stop = () => {
            if (animation.currentTween) {
                animation.currentTween.pause();
            }
            // Do not reset the frame; leave it where it paused
            if (animation.frameObj) {
                this.drawFrame(animation);
            }
        };

        // Mouse
        triggers.forEach((el) => {
            el.addEventListener("mouseenter", start);
            el.addEventListener("mouseleave", stop);
            // Keyboard accessibility
            el.addEventListener("focus", start, true);
            el.addEventListener("blur", stop, true);
            // Basic touch support
            el.addEventListener("touchstart", (e) => { start(); }, { passive: true });
            el.addEventListener("touchend", (e) => { stop(); }, { passive: true });
            el.addEventListener("touchcancel", (e) => { stop(); }, { passive: true });
        });
    }

    static initTweenLoop(animation, autoplay) {
        // Keep existing frame if present; otherwise start at 1
        animation.frameObj = animation.frameObj || { frame: 1 };
        // If we were exactly at the last frame, restart from 1 to avoid a no-op first cycle
        if (animation.frameObj.frame >= this.totalFrames) {
            animation.frameObj.frame = 1;
        }
        animation.currentTween = gsap.to(animation.frameObj, {
            frame: this.totalFrames,
            duration: 2,
            ease: "linear",
            repeat: -1, // loop indefinitely while running
            onUpdate: () => this.drawFrame(animation)
        });
    }

    static drawFrame(animation) {
        const index = Math.round(animation.frameObj.frame) - 1;
        const img = animation.images[index];
        if (!img) return;

        animation.ctx.clearRect(0, 0, animation.canvas.width, animation.canvas.height);

        const scale = Math.min(animation.canvas.width / img.width, animation.canvas.height / img.height);
        const x = (animation.canvas.width - img.width * scale) / 2;
        const y = (animation.canvas.height - img.height * scale) / 2;

        animation.ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
    }

    static resize() {
        if (!this.shapeAnimations) return;
        this.shapeAnimations.forEach((animation) => {
            animation.canvas.width = animation.clientWidth;
            animation.canvas.height = animation.clientHeight;
            this.drawFrame(animation);
        });
    }
}